
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MarketplaceStrategy } from "@/types/strategy-types";
import ProfileEditForm from "@/components/account/ProfileEditForm";
import RecommendedStrategies from "@/components/account/RecommendedStrategies";
import MyStrategies from "@/components/account/MyStrategies";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Sparkles, User, PlusCircle } from "lucide-react";

interface AccountTabsProps {
  activeTab: string;
  setActiveTab: (value: string) => void;
  userData: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    profileImageUrl: string | null;
  };
  updateProfileImage: (file: File) => Promise<string | null>;
  followedStrategies: MarketplaceStrategy[];
  recommendedStrategies: MarketplaceStrategy[];
  isLoadingStrategies: boolean;
  onSelectStrategy: (strategyName: string) => void;
  onUnfollowStrategy: (strategyId: string) => Promise<void>;
}

const AccountTabs = ({
  activeTab,
  setActiveTab,
  userData,
  updateProfileImage,
  followedStrategies,
  recommendedStrategies,
  isLoadingStrategies,
  onSelectStrategy,
  onUnfollowStrategy
}: AccountTabsProps) => {
  const handleProfileUpdate = async (formData: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in to update your profile");
        return;
      }

      // Update user metadata with new name
      const { error } = await supabase.auth.updateUser({
        data: {
          first_name: formData.firstName,
          last_name: formData.lastName
        }
      });

      if (error) {
        console.error("Error updating profile:", error);
        throw error;
      }

      toast.success("Profile updated successfully");
      
      // Refresh the page to show updated data
      window.location.reload();
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    }
  };

  const handleImageUpdate = async (file: File) => {
    if (!file) return null;
    return await updateProfileImage(file);
  };

  return (
    <Tabs defaultValue="my-strategies" value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="w-full grid grid-cols-3 mb-6">
        <TabsTrigger value="my-strategies" className="py-3">
          <Sparkles className="h-4 w-4 mr-2" />
          <span className="hidden sm:inline">My Strategies</span>
          <span className="sm:hidden">Strategies</span>
        </TabsTrigger>
        <TabsTrigger value="recommendations" className="py-3">
          <PlusCircle className="h-4 w-4 mr-2" />
          <span className="hidden sm:inline">Recommendations</span>
          <span className="sm:hidden">Recommended</span>
        </TabsTrigger>
        <TabsTrigger value="profile" className="py-3">
          <User className="h-4 w-4 mr-2" />
          <span>Profile</span>
        </TabsTrigger>
      </TabsList>
      
      <div className="p-1 sm:p-4">
        <TabsContent value="my-strategies" className="mt-0">
          <MyStrategies 
            strategies={followedStrategies}
            onSelectStrategy={onSelectStrategy}
            onUnfollow={onUnfollowStrategy}
            isLoading={isLoadingStrategies}
          />
        </TabsContent>
        
        <TabsContent value="recommendations" className="mt-0">
          <RecommendedStrategies 
            strategies={recommendedStrategies}
            onSelectStrategy={onSelectStrategy}
          />
        </TabsContent>
        
        <TabsContent value="profile" className="mt-0">
          <ProfileEditForm
            firstName={userData.firstName}
            lastName={userData.lastName}
            email={userData.email}
            profileImageUrl={userData.profileImageUrl}
            onSubmit={handleProfileUpdate}
            onUploadImage={handleImageUpdate}
          />
        </TabsContent>
      </div>
    </Tabs>
  );
};

export default AccountTabs;
